import type { TimelineVertex } from "./vertex-primitives";

interface Point {
  x: number;
  y: number;
}

/** One stroke along the polyline. Joins share corners so the outline stays closed. */
export function pushThickPolyline(
  verts: TimelineVertex[],
  points: readonly Point[],
  half: number,
  r: number,
  g: number,
  b: number,
  a: number,
): void {
  if (points.length < 2 || half <= 0) {
    return;
  }
  const left: Point[] = [];
  const right: Point[] = [];
  for (let i = 0; i < points.length; i++) {
    const curr = points[i];
    if (curr === undefined) {
      continue;
    }
    const off = joinOffset(points[i - 1], curr, points[i + 1], half);
    left.push({ x: curr.x + off.x, y: curr.y + off.y });
    right.push({ x: curr.x - off.x, y: curr.y - off.y });
  }
  for (let i = 1; i < left.length; i++) {
    const l0 = left[i - 1];
    const r0 = right[i - 1];
    const l1 = left[i];
    const r1 = right[i];
    if (l0 === undefined || r0 === undefined || l1 === undefined || r1 === undefined) {
      continue;
    }
    verts.push(
      { x: l0.x, y: l0.y, r, g, b, a },
      { x: r0.x, y: r0.y, r, g, b, a },
      { x: l1.x, y: l1.y, r, g, b, a },
      { x: l1.x, y: l1.y, r, g, b, a },
      { x: r0.x, y: r0.y, r, g, b, a },
      { x: r1.x, y: r1.y, r, g, b, a },
    );
  }
}

function joinOffset(
  prev: Point | undefined,
  curr: Point,
  next: Point | undefined,
  half: number,
): Point {
  const incoming = prev === undefined
    ? undefined
    : unit(curr.x - prev.x, curr.y - prev.y);
  const outgoing = next === undefined
    ? undefined
    : unit(next.x - curr.x, next.y - curr.y);
  const dirA = incoming ?? outgoing;
  const dirB = outgoing ?? incoming;
  if (dirA === undefined || dirB === undefined) {
    return { x: 0, y: 0 };
  }
  const n0 = { x: -dirA.y, y: dirA.x };
  const n1 = { x: -dirB.y, y: dirB.x };
  const mx = n0.x + n1.x;
  const my = n0.y + n1.y;
  const mlen = Math.hypot(mx, my);
  if (mlen < 1e-4) {
    return { x: n0.x * half, y: n0.y * half };
  }
  const nx = mx / mlen;
  const ny = my / mlen;
  const denom = nx * n0.x + ny * n0.y;
  const scale = Math.abs(denom) < 0.2 ? half : half / denom;
  const clamped = Math.min(Math.abs(scale), half * 4) * Math.sign(scale || 1);
  return { x: nx * clamped, y: ny * clamped };
}

function unit(x: number, y: number): Point | undefined {
  const len = Math.hypot(x, y);
  if (len < 1e-4) {
    return undefined;
  }
  return { x: x / len, y: y / len };
}
